import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { fetchThroughSleep, isGatewayStatus } from '../utils/wakeBackend';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function Login({ onSwitchToRegister, onBackToLanding }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login, statusMessage } = useAuth();
  const { teamSettings } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatusNote('');
    setLoading(true);

    const result = await login(email, password);

    if (!result.success) {
      setError(result.error);
    }

    setStatusNote('');
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    setForgotLoading(true);

    try {
      const response = await fetchThroughSleep(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: forgotEmail })
      }, {
        onStatus: (state) => setStatusNote(state === 'restarting'
          ? 'The server did not wake. Starting it back up…'
          : 'The server is asleep. Waking it…')
      });

      if (!response || isGatewayStatus(response.status)) {
        setForgotMessage({ type: 'error', text: 'The server is still starting. Try again in a minute.' });
        setForgotLoading(false);
        return;
      }

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setForgotMessage({ type: 'success', text: data.message });
      } else {
        setForgotMessage({ type: 'error', text: data.error || 'Failed to send reset email' });
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-dvh bg-theme-bg flex items-center justify-center p-6 safe-area-x pt-[max(1.5rem,env(safe-area-inset-top,0.5rem))]">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
          <h2 className="text-3xl font-bold text-theme-primary mb-6 text-center">
            {teamSettings?.displayName || 'Team Central'}
          </h2>
          <h3 className="text-xl font-semibold text-theme-primary mb-2">Forgot Password</h3>
          <p className="text-gray-600 mb-6 text-sm">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {statusNote && (
            <div className="bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 rounded mb-4">
              {statusNote}
            </div>
          )}

          {forgotMessage && (
            <div className={`px-4 py-3 rounded mb-4 ${
              forgotMessage.type === 'success'
                ? 'bg-green-100 border border-green-400 text-green-700'
                : 'bg-red-100 border border-red-400 text-red-700'
            }`}>
              {forgotMessage.text}
            </div>
          )}

          <form onSubmit={handleForgotPassword}>
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2 text-theme-primary">
                Email
              </label>
              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="border p-3 rounded w-full text-theme-primary bg-white focus-theme-primary"
                required
                disabled={forgotLoading}
              />
            </div>

            <button
              type="submit"
              className="w-full btn-theme-primary px-6 py-3 rounded-lg font-semibold disabled:bg-gray-400 disabled:opacity-50"
              disabled={forgotLoading}
            >
              {forgotLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setForgotMessage('');
                setForgotEmail('');
              }}
              className="text-theme-primary font-semibold hover:underline"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-theme-bg flex items-center justify-center p-4 sm:p-6 safe-area-x pt-[max(1.5rem,env(safe-area-inset-top,0.5rem))]">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg w-full max-w-md">
        {onBackToLanding && (
          <button
            onClick={onBackToLanding}
            className="flex items-center gap-2 text-gray-600 hover:text-theme-primary mb-4 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
        )}
        <h2 className="text-2xl sm:text-3xl font-bold text-theme-primary mb-4 sm:mb-6 text-center">
          {teamSettings?.displayName || 'Central'}
        </h2>
        <h3 className="text-lg sm:text-xl font-semibold text-theme-primary mb-4 sm:mb-6">Login</h3>

        {(statusNote || statusMessage) && (
          <div className="bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 rounded mb-4">
            {statusNote || statusMessage}
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2 text-theme-primary">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border p-3 rounded w-full text-theme-primary bg-white focus-theme-primary"
              required
              disabled={loading}
            />
          </div>

          <div className="mb-2">
            <label className="block text-sm font-semibold mb-2 text-theme-primary">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border p-3 rounded w-full text-theme-primary bg-white focus-theme-primary"
              required
              disabled={loading}
            />
          </div>

          <div className="mb-6 text-right">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-gray-600 hover:text-theme-primary hover:underline"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            className="w-full btn-theme-primary px-6 py-3 rounded-lg font-semibold disabled:bg-gray-400 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (statusNote || statusMessage || 'Logging in...') : 'Login'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <button
              onClick={onSwitchToRegister}
              className="text-theme-primary font-semibold hover:underline"
            >
              Register
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
